import { useEffect, useState } from "react";
import { api, unwrapObject } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { fmtQty, MOVEMENT_LABELS, GRADES, type StockUnitRecord } from "@/types/inventory";
import { StockStatusBadge, Swatch } from "./StockStatusBadge";

const when = (d: string) => new Date(d).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit" });

/** One roll: where it is, what is left, and every movement it has had */
export function UnitHistoryDialog({ unitId, onClose }: { unitId: string | null; onClose: () => void }) {
  const [unit, setUnit] = useState<StockUnitRecord | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!unitId) return;
    setUnit(null);
    setLoading(true);
    api
      .get(`/api/Inventory/UnitById/${unitId}`)
      .then((r) => setUnit(unwrapObject<StockUnitRecord>(r)))
      .catch(() => setUnit(null))
      .finally(() => setLoading(false));
  }, [unitId]);

  return (
    <Dialog open={!!unitId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{unit ? `Roll ${unit.unit_no}` : "Roll history"}</DialogTitle>
          <DialogDescription>{unit ? `${unit.product?.name || ""}${unit.variant ? ` · ${unit.variant.name}` : ""} · Lot ${unit.lot?.lot_number || "-"}` : "Loading…"}</DialogDescription>
        </DialogHeader>
        {loading && (
          <div className="py-10 flex justify-center">
            <Loader2 className="size-5 animate-spin text-muted-foreground" />
          </div>
        )}
        {unit && (
          <div className="space-y-4 text-sm overflow-y-auto max-h-[68vh] pr-1">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
              <div><p className="text-muted-foreground">Status</p><StockStatusBadge status={unit.unit_status} /></div>
              <div><p className="text-muted-foreground">Left / received</p><p className="font-semibold">{fmtQty(unit.quantity, unit.unit)} / {fmtQty(unit.initial_quantity)}</p></div>
              <div><p className="text-muted-foreground">Warehouse</p><p className="font-semibold">{unit.warehouse?.name}{unit.location ? ` · ${unit.location}` : ""}</p></div>
              <div><p className="text-muted-foreground">Grade</p><p className="font-semibold">{GRADES[unit.grade] || unit.grade}</p></div>
              <div><p className="text-muted-foreground">Shade</p><p className="font-semibold"><Swatch hex={unit.variant?.color_hex} />{unit.variant?.name || "-"}</p></div>
              <div><p className="text-muted-foreground">Net weight</p><p className="font-semibold">{fmtQty(unit.net_weight, "kg")}</p></div>
              <div><p className="text-muted-foreground">Gross weight</p><p className="font-semibold">{fmtQty(unit.gross_weight, "kg")}</p></div>
            </div>
            <div className="rounded-lg border border-border overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-muted-foreground border-b border-border">
                    <th className="px-3 py-2">When</th>
                    <th className="px-3 py-2">What</th>
                    <th className="px-3 py-2">Where</th>
                    <th className="px-3 py-2 text-right">Change</th>
                    <th className="px-3 py-2 text-right">Roll holds</th>
                    <th className="px-3 py-2">Document / by</th>
                  </tr>
                </thead>
                <tbody>
                  {(unit.movements || []).map((m) => (
                    <tr key={m.id} className="border-b border-border/60 last:border-0">
                      <td className="px-3 py-2 whitespace-nowrap">{when(m.created_on)}</td>
                      <td className="px-3 py-2">{MOVEMENT_LABELS[m.movement_type] || m.movement_type}{m.reason ? <div className="text-muted-foreground">{m.reason}</div> : null}</td>
                      <td className="px-3 py-2">{m.warehouse_name}</td>
                      <td className={`px-3 py-2 text-right font-semibold ${m.quantity < 0 ? "text-rose-600" : "text-emerald-600"}`}>{m.quantity > 0 ? "+" : ""}{fmtQty(m.quantity)}</td>
                      <td className="px-3 py-2 text-right">{fmtQty(m.balance_after)}</td>
                      <td className="px-3 py-2"><code className="font-mono">{m.ref_number || "-"}</code>{m.created_by_name ? <div className="text-muted-foreground">{m.created_by_name}</div> : null}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
